import { ErrorModal } from "@dexteel/mesf-core";
import { Alert, Grid2, Paper, Snackbar, Typography } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useRef, useState } from "react";

import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { CenteredLazyLoading } from "@/controls/LazyLoading";

import { Rollset, StandNameKeys } from "@/pages/rollshop/models/Rollset";
import { routeRollsets } from "@/setup/routes/rollshop.routes";
import { SectionSelector } from "@/shared/selectors";
import { useSections } from "@/shared/selectors/SectionsOfPartSelector";
import { HelmetDexteel } from "@dexteel/mesf-core";
import { useNavigate, useParams } from "react-router-dom";
import { useReadyRollsets } from "../hooks/useReadyRollsets";
import { useTableData } from "../hooks/useTableData";
import { RollsetAssemblyModal } from "./RollsetAssemblyModal";
import { RollStatusInfo } from "./RollStatusInfo";

export interface RollsetInfo {
	StandName: string;
	StandId: number;
	Letter: string;

	TopRollId: number | null;
	TopRollName: string;
	TopRollLocationKey: string;
	TopRollDiameter: number | null;
	TopRollStandId: number | null;
	TopRollType: string | null;

	BottomRollId: number | null;
	BottomRollName: string;
	BottomRollLocationKey: string;
	BottomRollDiameter: number | null;
	BottomRollStandId: number | null;
	BottomRollType: string | null;

	RollsetStatusCode: string | null;
}

const stands = {
	10: "F1",
	11: "F2",
	12: "F3",
	13: "F4",
	14: "F5",
	15: "F6",
	16: "F7",
	17: "R1",
	18: "R2",
	19: "R3",
	20: "R4",
};

export type RollsetTableRow = {
	[standName in StandNameKeys]: RollsetInfo;
};
export const TableRollset = () => {
	const { sectionId: sectionIdParam } = useParams();
	const navigate = useNavigate();

	const isFollowingCurrent = sectionIdParam === "current";

	const prevCurrentSectionIdRef = useRef<number | null>(null);
	const [sectionId, setSectionId] = useState(0);
	const [currentChangedOpen, setCurrentChangedOpen] = useState(false);
	const [sectionName, setSectionName] = useState("");
	const [selectedCell, setSelectedCell] = useState<RollsetInfo | null>(null);
	const [showModal, setShowModal] = useState(false);
	const [error, setError] = useState("");

	const {
		data: rows,
		error: e,
		isError,
		isLoading,
	} = useReadyRollsets(sectionId);

	const { data: sectionsData } = useSections({});
	const currentSectionId =
		sectionsData?.upcomingEvents?.find((e) => e.EventIndex === 1)?.SectionId ??
		null;

	const { columnDefs } = useTableData();
	const { defaultColDef } = useGridDefinitions({});

	function transformData(data: Rollset[]): any[] {
		const letras = new Set<string>();

		data.forEach((item) => {
			letras.add(item.RollsetLetter);
		});

		const standsArray = Object.entries(stands);

		const letrasArray = Array.from(letras).sort();

		const requiredLetters = ["A", "B", "C", "D", "E", "F", "G"];
		requiredLetters.forEach((letter) => {
			if (!letrasArray.includes(letter)) {
				letrasArray.push(letter);
			}
		});
		letrasArray.sort();

		const result: RollsetTableRow[] = [];

		letrasArray.forEach((letter) => {
			const row: RollsetTableRow = {} as any;

			standsArray.forEach(([standId, standName]) => {
				const item = data.find(
					(item) =>
						item.StandId === Number(standId) && item.RollsetLetter === letter,
				);

				row[standName as StandNameKeys] = {
					StandId: Number(standId),
					StandName: standName,
					Letter: letter,
					TopRollId: null,
					TopRollName: "",
					TopRollLocationKey: "",
					TopRollDiameter: null,
					TopRollStandId: null,
					TopRollType: null,
					BottomRollId: null,
					BottomRollName: "",
					BottomRollLocationKey: "",
					BottomRollDiameter: null,
					BottomRollStandId: null,
					BottomRollType: null,
					RollsetStatusCode: "E",
				};

				if (item) {
					row[standName as StandNameKeys] = {
						...row[standName as StandNameKeys],
						TopRollId: item.TopRollId || null,
						TopRollName: item.TopRollName || "",
						TopRollLocationKey: item.TopRollLocationKey,
						TopRollDiameter: item.TopRollDiameter || null,
						TopRollStandId: item.TopRollStandId || null,
						TopRollType: item.TopRollType || null,
						BottomRollId: item.BottomRollId || null,
						BottomRollName: item.BottomRollName || "",
						BottomRollLocationKey: item.BottomRollLocationKey,
						BottomRollDiameter: item.BottomRollDiameter || null,
						BottomRollStandId: item.BottomRollStandId || null,
						RollsetStatusCode: item.RollsetStatusCode || "E",
						BottomRollType: item.BottomRollType || null,
					};
				}
			});
			result.push(row);
		});

		return result;
	}

	const formatedRows: RollsetTableRow[] = transformData(rows || []);

	useEffect(() => {
		if (isError) {
			setError(e.message);
		}
	}, [isError, e]);

	useEffect(() => {
		if (isFollowingCurrent && currentSectionId) {
			if (
				prevCurrentSectionIdRef.current !== null &&
				prevCurrentSectionIdRef.current !== currentSectionId
			) {
				setCurrentChangedOpen(true);
			}
			prevCurrentSectionIdRef.current = currentSectionId;
			setSectionId(currentSectionId);
		}
	}, [isFollowingCurrent, currentSectionId]);

	useEffect(() => {
		if (!sectionIdParam) {
			navigate(`/${routeRollsets}/current`, { replace: true });
		} else if (!isFollowingCurrent) {
			setSectionId(Number(sectionIdParam));
		}
	}, [sectionIdParam, isFollowingCurrent]);

	return (
		<>
			{rows && rows?.length > 0 && (
				<HelmetDexteel title={`Rollsets - Section ${sectionName}`} />
			)}
			<Grid2 container justifyContent="center" p={2} spacing={1}>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Typography variant="h5" fontWeight={600}>
						Rollsets
					</Typography>
				</Grid2>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<SectionSelector
						label="For Section"
						value={sectionId}
						onChange={(val: number | null, name?: string | null) => {
							navigate(`/${routeRollsets}/${val}`);
							setSectionId(val ?? 0);
							setSectionName(name ?? "");
						}}
						includeMostUsed
						onCurrentClick={() => navigate(`/${routeRollsets}/current`)}
					/>
				</Grid2>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Paper elevation={2} style={{ height: "70vh" }}>
						<AgGridReact<RollsetTableRow>
							loading={isLoading}
							rowData={formatedRows || []}
							columnDefs={columnDefs as any}
							defaultColDef={defaultColDef}
							rowHeight={90}
							headerHeight={34}
							animateRows
							loadingOverlayComponent={CenteredLazyLoading}
							columnHoverHighlight
							onCellClicked={(params) => {
								if (sectionId) {
									if (params.data && params.colDef?.field) {
										const rollsetByStand: RollsetInfo =
											params.data[params.colDef!.field as StandNameKeys];
										if (typeof rollsetByStand == "object") {
											setSelectedCell(rollsetByStand);
											setShowModal(true);
										}
									}
								}
							}}
							gridOptions={{
								rowSelection: {
									mode: "singleRow",
									checkboxes: false,
									enableClickSelection: false,
								},
							}}
						/>
					</Paper>
				</Grid2>
				<Grid2 size={{ md: 12 }}>
					<RollStatusInfo />
				</Grid2>
			</Grid2>
			<ErrorModal error={error} onHide={() => setError("")} />
			<RollsetAssemblyModal
				sectionId={sectionId}
				rollset={selectedCell}
				show={showModal}
				onHide={() => {
					setShowModal(false);
				}}
			/>
			<Snackbar
				open={currentChangedOpen}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert
					severity="info"
					variant="filled"
					onClose={() => setCurrentChangedOpen(false)}
					sx={{
						bgcolor: "grey.700",
						color: "common.white",
					}}
				>
					The current section has changed.
				</Alert>
			</Snackbar>
		</>
	);
};
